// Comprehensive hook for room configuration and commonly derived values

import { useMemo } from 'react';
import { useRoomStateSelector } from './useRoomStateSelector';

/**
 * Comprehensive hook that provides room configuration and commonly derived values
 * Reduces code redundancy by centralizing common room state selections
 */
export const useRoomConfig = () => {
  const roomCode = useRoomStateSelector((state) => state?.roomCode);
  const status = useRoomStateSelector((state) => state?.status);
  const gameConfig = useRoomStateSelector((state) => state?.gameConfig);
  const host = useRoomStateSelector((state) => state?.host);
  const turnsRemaining = useRoomStateSelector(
    (state) => state?.turnsRemaining ?? 0
  );
  const lastEventVersion = useRoomStateSelector(
    (state) => state?.lastEventVersion
  );

  // Commonly derived values
  const { gameId, gameName, totalTurns, currentTurnIndex } = useMemo(
    () => ({
      gameId: gameConfig?.gameId,
      gameName: gameConfig?.gameName,
      totalTurns: gameConfig?.gameTotalTurns || 5, // Fallback is a bit arbitrary, but would work for Meme Mash
      currentTurnIndex: (gameConfig?.gameTotalTurns || 5) - turnsRemaining - 1, // 0-based indexing for arrays
    }),
    [gameConfig, turnsRemaining]
  );
  const { accent, contentStyle, language } = useMemo(
    () => ({
      accent: host?.userPreferences?.accent || 'en-US',
      contentStyle: host?.userPreferences?.contentStyle || 'standard',
      language: host?.userPreferences?.language || 'en',
    }),
    [host]
  );
  const {
    censorCommon,
    censorCustom,
    censorRigid,
    censorCustomWords,
    twitchTermsBlocked,
    censorTwitchWords,
    twitchPlayersOnly,
    disallowBlockedAndBanned,
    blockedTwitchUserIds,
  } = useMemo(
    () => ({
      censorCommon: host?.modSettings?.censorCommon,
      censorCustom: host?.modSettings?.censorCustom,
      censorRigid: host?.modSettings?.censorRigid,
      censorCustomWords: host?.modSettings?.censorCustomWords ?? [],
      twitchTermsBlocked: host?.modSettings?.twitchTermsBlocked ?? false,
      censorTwitchWords: host?.modSettings?.censorTwitchWords ?? [],
      twitchPlayersOnly: host?.modSettings?.twitchPlayersOnly,
      disallowBlockedAndBanned:
        host?.modSettings?.disallowBlockedAndBanned ?? false,
      blockedTwitchUserIds: host?.modSettings?.blockedTwitchUserIds ?? [],
    }),
    [host]
  );

  return useMemo(
    () => ({
      // Basic room state
      roomCode,
      status,
      gameConfig,
      host,
      turnsRemaining,
      lastEventVersion,

      // Commonly derived values
      gameId,
      gameName,
      totalTurns,
      currentTurnIndex,
      accent,
      contentStyle,
      language,
      censorCommon,
      censorCustom,
      censorRigid,
      censorCustomWords,
      twitchTermsBlocked,
      censorTwitchWords,
      twitchPlayersOnly,
      disallowBlockedAndBanned,
      blockedTwitchUserIds,
    }),
    [
      roomCode,
      status,
      gameConfig,
      host,
      turnsRemaining,
      lastEventVersion,
      gameId,
      gameName,
      totalTurns,
      currentTurnIndex,
      accent,
      contentStyle,
      language,
      censorCommon,
      censorCustom,
      censorRigid,
      censorCustomWords,
      twitchTermsBlocked,
      censorTwitchWords,
      twitchPlayersOnly,
      disallowBlockedAndBanned,
      blockedTwitchUserIds,
    ]
  );
};

export default useRoomConfig;
